import { css } from '@emotion/react';
import { Box } from '@mui/material';

import FilterIcon from '~/assets/icons/svgs/filter.svg?react';
import { Text } from '~/components/ui/text';
import { COLORS } from '~/styles/colors';
import { mainPreviewContainerStyles } from '~/styles/common';
import { cutString } from '~/utils/common';

const tripleItem = [0, 1, 2];

type WebsiteMockupProps = {
  url?: string;
  isAnonymous?: boolean;
};

export const WebsiteMockup = ({ url = 'https://example.com', isAnonymous = false }: WebsiteMockupProps) => {
  const styles = useStyles({ isAnonymous });

  return (
    <Box sx={{ ...mainPreviewContainerStyles, alignItems: 'flex-start' }}>
      <Box sx={styles.topBlue} className={isAnonymous ? 'anonymous' : undefined} />
      <Box sx={styles.content} className={isAnonymous ? 'anonymous' : undefined}>
        <Box sx={styles.middle} className={isAnonymous ? 'anonymous' : undefined}>
          <Box sx={styles.topGrey} className={isAnonymous ? 'anonymous' : undefined} />
          <Box sx={styles.tripleContainer}>
            {tripleItem.map((item) => (
              <Box key={item} sx={styles.tripleItem} className={isAnonymous ? 'anonymous' : undefined} />
            ))}
          </Box>
          <Box sx={styles.circles}>
            {tripleItem.map((item) => (
              <Box key={item} sx={styles.circle} className={isAnonymous ? 'anonymous' : undefined} />
            ))}
          </Box>
          <Box sx={styles.bottomBlueContainer}>
            <Box sx={styles.bottomBlue} />
          </Box>
        </Box>

        <Box sx={styles.urlContainer} className={isAnonymous ? 'anonymous' : undefined}>
          <FilterIcon width={15} height={15} />
          <Text sx={styles.text} className={isAnonymous ? 'anonymous' : undefined}>
            {cutString(url, 0, 30)}
          </Text>
        </Box>
      </Box>
    </Box>
  );
};

function useStyles() {
  return {
    topBlue: css`
      height: 60px;
      width: calc(100% - 4px);
      margin: 34px auto 0;
      background-color: ${COLORS.primary};

      &.anonymous {
        height: 80px;
        width: calc(100% - 8px);
        margin-top: 48px;
      }
    `,
    middle: css`
      position: relative;
      padding: 8px;
      border-radius: 13px;
      background-color: ${COLORS.white};
      display: flex;
      flex-direction: column;
      gap: 26px;
      width: calc(100% - 20px);
      margin: 0 10px;
      top: -40px;

      &.anonymous {
        top: -50px;
      }
    `,
    topGrey: css`
      height: 172px;
      background-color: ${COLORS.grey700};
      border-radius: 10px;

      &.anonymous {
        height: 190px;
      }
    `,
    tripleContainer: css`
      display: flex;
      flex-direction: column;
      gap: 5px;
    `,
    tripleItem: css`
      border-radius: 10px;
      height: 15px;
      background-color: ${COLORS.grey700};

      &.anonymous {
        height: 18px;
      }
    `,
    circles: css`
      display: flex;
      justify-content: center;
      gap: 13px;
    `,
    circle: css`
      width: 50px;
      height: 50px;
      border-radius: 50%;
      background-color: ${COLORS.grey700};

      &.anonymous {
        width: 56px;
        height: 56px;
      }
    `,
    bottomBlueContainer: css`
      background-color: ${COLORS.blue50};
      border-radius: 10px;
      padding: 13px;
    `,
    bottomBlue: css`
      height: 18px;
      background-color: ${COLORS.primary};
      border-radius: 6px;
    `,
    content: css`
      background-color: ${COLORS.grey700};
      width: calc(100% - 4px);
      height: calc(100% - 94px);
      border-radius: 0 0 35px 35px;
      margin: 0 auto;

      &.anonymous {
        height: calc(100% - 132px);
      }
    `,
    urlContainer: css`
      position: relative;
      top: -40px;
      border: 1px solid ${COLORS.textMain};
      width: calc(100% - 20px);
      margin: 20px 10px 0;
      border-radius: 65px;
      height: 32px;
      box-sizing: border-box;
      color: ${COLORS.disableGrayColor};
      padding: 8px 13px;
      display: flex;
      gap: 4px;

      svg {
        flex-shrink: 0;
      }

      &.anonymous {
        height: 40px;
      }
    `,
    text: css`
      font-size: 12px;
      line-height: 14px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;

      &.anonymous {
        font-size: 15px;
        line-height: 20px;
      }
    `,
  };
}
